/**
 * Matches and Chat Module
 * Handles user matches, chat functionality, and messaging
 */
import { MatchesAPI } from '../services/api.js';
import { AppState, updateState, normalizeImageUrl } from '../config.js';
import { showToast, Navigation, Utils } from '../utils/ui.js';
import { joinChat as socketJoinChat, leaveChat as socketLeaveChat, sendMessage as socketSendMessage, emitTyping, emitStopTyping, markRead, isConnected } from '../services/socket.js';
import { t } from '../utils/i18n.js';
import { renderGameMessage, openGamePicker, bindGameCardEvents, setupGameEventListeners } from './games.js';
import { updateNotificationBadge } from './home.js';
import { haptic } from '../utils/haptics.js';

function scrollChatToBottom() {
    const el = document.getElementById('messages-container');
    if (el) requestAnimationFrame(() => { el.scrollTop = el.scrollHeight; });
}

/**
 * Load and display user matches
 */
export async function loadMatches() {
    try {
        const matchesGrid = document.getElementById('matches-grid');
        matchesGrid.innerHTML = '';
        matchesGrid.classList.remove('stagger-in');

        // Clear matches nav badge since user is viewing matches
        const matchesNavBadge = document.getElementById('matches-nav-badge');
        if (matchesNavBadge) matchesNavBadge.classList.add('hidden');

        const response = await MatchesAPI.getMatches();

        if (response.matches.length === 0) {
            matchesGrid.innerHTML = `
                <div class="text-center py-12">
                    <i class="fas fa-heart text-4xl text-muted-foreground mb-4 float-animation pop-in"></i>
                    <h3 class="text-lg font-medium text-foreground mb-2">${Utils.escapeHtml(t('matches.noMatchesYet'))}</h3>
                    <p class="text-muted-foreground">${Utils.escapeHtml(t('matches.startSwiping'))}</p>
                    <button onclick="navigateToPage('home')" class="mt-4 shadcn-button shadcn-button-primary btn-press breathe-glow">
                        ${Utils.escapeHtml(t('matches.findEvents'))}
                    </button>
                </div>
            `;
            return;
        }

        // Add stagger-in before content so new items are born at opacity 0
        matchesGrid.classList.add('stagger-in');
        matchesGrid.innerHTML = response.matches.map(match => createMatchCard(match)).join('');

    } catch (error) {
        console.error('Failed to load matches:', error);
        showToast(t('matches.failedLoadMatches'), 'error');
    }
}

/**
 * Create match card HTML (messaging-app style - vertical list)
 */
function createMatchCard(match) {
    const profileImages = match.customer.profile_images || [];
    const mainImage = profileImages[0] || '';
    const hasUnread = match.unread_count > 0;

    // Check if match is less than 1 hour old for new-match glow
    const matchAge = Date.now() - new Date(match.matched_at).getTime();
    const isNewMatch = matchAge < 60 * 60 * 1000;

    const avatarContent = mainImage
        ? `<img src="${normalizeImageUrl(mainImage)}" alt="${Utils.escapeHtml(match.customer.name)}" class="w-full h-full object-cover">`
        : `<span class="text-white font-bold text-lg">${Utils.escapeHtml(match.customer.name[0].toUpperCase())}</span>`;

    return `
        <div class="glass app-glass-card rounded-2xl p-4 cursor-pointer transition-all hover:bg-white/5 group press-scale${isNewMatch ? ' match-card-new' : ''}${hasUnread ? ' match-card-unread' : ''}"
             onclick="openChat(${parseInt(match.id)})">
            <div class="flex items-center gap-4">
                <!-- Avatar with gradient ring -->
                <div class="relative flex-shrink-0">
                    <div class="w-14 h-14 rounded-full overflow-hidden" style="padding: 2px; background: var(--gradient-primary);">
                        <div class="w-full h-full rounded-full overflow-hidden bg-card flex items-center justify-center" style="background: ${mainImage ? 'transparent' : '#FF00FF'};">
                            ${avatarContent}
                        </div>
                    </div>
                    ${match.is_online ? `<!-- Online indicator with glow -->
                    <div class="absolute bottom-0 right-0 w-3.5 h-3.5 bg-green-500 border-2 border-card rounded-full" style="animation: onlineGlow 2s ease-in-out infinite;"></div>` : ''}
                </div>

                <!-- Info -->
                <div class="flex-1 min-w-0">
                    <div class="flex items-center justify-between mb-1">
                        <h4 class="${hasUnread ? 'font-bold' : 'font-semibold'} text-foreground truncate">${Utils.escapeHtml(match.customer.name)}</h4>
                        <span class="text-xs text-muted-foreground flex-shrink-0 ml-2">
                            ${Utils.formatRelativeDate(match.matched_at)}
                        </span>
                    </div>
                    <div class="flex items-center justify-between">
                        <p class="text-sm text-muted-foreground truncate">
                            <i class="fas fa-map-marker-alt mr-1 text-xs"></i>${Utils.escapeHtml(match.event.name)}
                        </p>
                        ${hasUnread ? `
                            <span class="flex-shrink-0 ml-2 min-w-[22px] h-[22px] rounded-full flex items-center justify-center text-xs font-bold text-white glow-pulse" style="background: var(--gradient-warm);">
                                ${match.unread_count > 99 ? '99+' : match.unread_count}
                            </span>
                        ` : ''}
                    </div>
                </div>

                <!-- Arrow -->
                <i class="fas fa-chevron-right text-muted-foreground/40 group-hover:text-primary transition-colors flex-shrink-0"></i>
            </div>
        </div>
    `;
}

/**
 * Open chat interface for a specific match
 */
export function openChat(matchId) {
    haptic('press');

    // Leave previous chat room if any
    if (AppState.currentMatch && AppState.currentMatch.id !== matchId) {
        socketLeaveChat(AppState.currentMatch.id);
    }

    updateState({ currentMatch: { id: matchId } });
    loadChatData(matchId);
    window.navigateToPage('chat');

    // Join socket room and mark as read
    socketJoinChat(matchId);
    markRead(matchId);
}

/**
 * Load chat data and messages for a match
 */
async function loadChatData(matchId) {
    try {
        const response = await MatchesAPI.getMessages(matchId);

        // Update chat header - we need to get match details
        const matchesResponse = await MatchesAPI.getMatches();
        const matchDetails = matchesResponse.matches.find(m => m.id == matchId);

        if (matchDetails) {
            document.getElementById('chat-partner-name').textContent = matchDetails.customer.name;
            document.getElementById('chat-partner-initial').textContent = matchDetails.customer.name[0].toUpperCase();
            document.getElementById('chat-event-info').textContent = matchDetails.event.name;
            // Store partner name for game renderers
            window._currentChatPartnerName = matchDetails.customer.name;
        }

        // Load messages
        const messagesContainer = document.getElementById('messages-container');
        messagesContainer.innerHTML = response.messages.map(message => createMessageBubble(message)).join('');

        // Bind game card events after rendering
        bindGameCardEvents();

        // Scroll to bottom
        scrollChatToBottom();

    } catch (error) {
        console.error('Failed to load chat:', error);
        showToast(t('matches.failedLoadMessages'), 'error');
    }
}

/**
 * Create message bubble HTML
 */
function createMessageBubble(message) {
    const isOwn = message.sender.id == AppState.currentUser.id;

    // Handle game message types
    if (['game_invite', 'game_action', 'game_result'].includes(message.message_type)) {
        return renderGameMessage(message, isOwn);
    }

    const bubbleClass = isOwn ? 'message-sent' : 'message-received';

    return `
        <div class="flex ${isOwn ? 'justify-end' : 'justify-start'} mb-4">
            <div class="message-bubble ${bubbleClass}">
                <p class="text-sm">${Utils.escapeHtml(message.message_text)}</p>
                <span class="text-xs opacity-75 mt-1 block">
                    ${Utils.formatTime(message.createdAt || message.created_at)}
                </span>
            </div>
        </div>
    `;
}

/**
 * Handle sending a new message
 */
let typingTimeout = null;
let isSending = false;

async function handleSendMessage(e) {
    e.preventDefault();

    if (isSending) return;

    const messageInput = document.getElementById('message-input');
    const messageText = messageInput.value.trim();

    if (!messageText || !AppState.currentMatch) return;

    isSending = true;
    const sendBtn = document.getElementById('send-btn');
    if (sendBtn) sendBtn.disabled = true;

    // Stop typing indicator
    emitStopTyping(AppState.currentMatch.id);
    haptic('send');

    // Try socket first, fallback to REST
    if (isConnected()) {
        socketSendMessage(AppState.currentMatch.id, messageText);
        messageInput.value = '';

        // Optimistic display - show own message immediately
        const messagesContainer = document.getElementById('messages-container');
        if (messagesContainer) {
            messagesContainer.innerHTML += createMessageBubble({
                sender: { id: AppState.currentUser.id },
                message_text: messageText,
                created_at: new Date().toISOString()
            });
            scrollChatToBottom();
        }

        isSending = false;
        if (sendBtn) sendBtn.disabled = false;
    } else {
        try {
            const response = await MatchesAPI.sendMessage(AppState.currentMatch.id, messageText);

            if (response.success) {
                messageInput.value = '';

                // Add message to chat immediately (no socket, so add manually)
                const messagesContainer = document.getElementById('messages-container');
                messagesContainer.innerHTML += createMessageBubble({
                    sender: { id: AppState.currentUser.id },
                    message_text: messageText,
                    created_at: new Date().toISOString()
                });

                scrollChatToBottom();
            }
        } catch (error) {
            console.error('Failed to send message:', error);
            showToast(t('matches.failedSendMessage'), 'error');
        } finally {
            isSending = false;
            if (sendBtn) sendBtn.disabled = false;
        }
    }
}

/**
 * Handle typing indicator input events
 */
function handleTypingInput() {
    if (!AppState.currentMatch) return;

    emitTyping(AppState.currentMatch.id);

    // Clear previous timeout and set new one
    if (typingTimeout) clearTimeout(typingTimeout);
    typingTimeout = setTimeout(() => {
        emitStopTyping(AppState.currentMatch.id);
    }, 2000);
}

/**
 * Setup matches and chat event listeners
 */
let matchesListenersInitialized = false;

export function setupMatchesEventListeners() {
    if (matchesListenersInitialized) return;
    matchesListenersInitialized = true;

    // Message form
    const messageForm = document.getElementById('message-form');
    if (messageForm) {
        messageForm.addEventListener('submit', handleSendMessage);
    }

    // Typing indicator on message input
    const messageInput = document.getElementById('message-input');
    if (messageInput) {
        messageInput.addEventListener('input', handleTypingInput);
    }

    // Game button
    const gameBtn = document.getElementById('game-btn');
    if (gameBtn) {
        gameBtn.addEventListener('click', () => {
            openGamePicker();
        });
    }

    // Back to matches button — use history.back() so the history stack stays clean
    const backBtn = document.getElementById('back-to-matches');
    if (backBtn) {
        backBtn.addEventListener('click', () => {
            // Leave socket room when going back
            if (AppState.currentMatch) {
                socketLeaveChat(AppState.currentMatch.id);
            }
            window._currentChatPartnerName = '';
            history.back();
        });
    }

    // Setup game event listeners
    setupGameEventListeners();

    // Socket.io real-time event listeners
    window.addEventListener('socket:new_message', (e) => {
        const { matchId, message } = e.detail;
        // Skip own messages (already displayed optimistically)
        if (message.sender?.id === AppState.currentUser?.id) return;
        // Add to chat if we're viewing this match
        if (AppState.currentMatch && AppState.currentMatch.id == matchId) {
            const messagesContainer = document.getElementById('messages-container');
            if (messagesContainer) {
                messagesContainer.innerHTML += createMessageBubble(message);
                // Bind game card events for newly added game messages
                bindGameCardEvents();
                scrollChatToBottom();
            }
            // Hide typing indicator
            const typingEl = document.getElementById('typing-indicator');
            if (typingEl) typingEl.classList.add('hidden');
        } else {
            // Not viewing this chat — show toast and update badge
            const senderName = message.sender?.name || message.senderName || 'Someone';
            const preview = message.content?.substring(0, 50) || '';
            showToast(`${senderName}: ${preview}`, 'info');
            updateNotificationBadge();
        }
    });

    window.addEventListener('socket:typing', (e) => {
        const { matchId, customerName } = e.detail;
        if (AppState.currentMatch && AppState.currentMatch.id == matchId) {
            const typingEl = document.getElementById('typing-indicator');
            const typingName = document.getElementById('typing-name');
            if (typingEl && typingName) {
                typingName.textContent = customerName;
                typingEl.classList.remove('hidden');
            }
        }
    });

    window.addEventListener('socket:stop_typing', (e) => {
        const { matchId } = e.detail;
        if (AppState.currentMatch && AppState.currentMatch.id == matchId) {
            const typingEl = document.getElementById('typing-indicator');
            if (typingEl) typingEl.classList.add('hidden');
        }
    });

    window.addEventListener('socket:message_notification', (e) => {
        const { matchId, senderName, preview } = e.detail;
        // Show toast if not viewing this chat
        if (!AppState.currentMatch || AppState.currentMatch.id != matchId) {
            showToast(`${senderName || 'Someone'}: ${preview}`, 'info');
            updateNotificationBadge();
        }
    });

    // Update match list when a match partner comes online/offline
    window.addEventListener('socket:presence_change', () => {
        const matchesPage = document.getElementById('matches-page');
        if (matchesPage && matchesPage.classList.contains('active')) {
            loadMatches();
        }
    });

    // Re-join match room and catch up on missed messages after socket reconnect
    window.addEventListener('socket:connected', () => {
        if (AppState.currentMatch) {
            socketJoinChat(AppState.currentMatch.id);
            loadChatData(AppState.currentMatch.id);
        }
    });

    // Handle foreground push notifications for chat updates
    navigator.serviceWorker?.addEventListener('message', (event) => {
        if (event.data?.type === 'PUSH_NOTIFICATION') {
            const pushData = event.data.payload?.data;
            if (pushData?.type === 'message' && AppState.currentMatch &&
                String(pushData.matchId) === String(AppState.currentMatch.id)) {
                loadChatData(AppState.currentMatch.id);
            }
        }
    });
}

// Make functions globally available for HTML onclick handlers
window.openChat = openChat;
